import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ordersAPI } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Package, MapPin, CreditCard, ArrowLeft } from 'lucide-react';
import Layout from '@/components/Layout';
import { toast } from 'sonner';

const OrderDetailPage = () => {
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    loadOrder();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const loadOrder = async () => {
    try {
      const data = await ordersAPI.getById(orderId);
      setOrder(data);
    } catch (error) {
      toast.error('Order not found');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      processing: 'text-blue-600 bg-blue-50',
      shipped: 'text-purple-600 bg-purple-50',
      delivered: 'text-green-600 bg-green-50',
      cancelled: 'text-red-600 bg-red-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'text-yellow-600 bg-yellow-50',
      completed: 'text-green-600 bg-green-50',
      failed: 'text-red-600 bg-red-50'
    };
    return colors[status] || 'text-gray-600 bg-gray-50';
  };

  if (loading) {
    return (
      <Layout>
        <div className="section-padding">
          <div className="container-custom">
            <div className="skeleton h-96 rounded-2xl"></div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) return null;

  // Older orders (created before subtotal/shipping_fee tracking was added)
  // won't have these fields - fall back gracefully instead of showing NaN.
  const subtotal = order.subtotal ?? order.total;
  const shippingFee = order.shipping_fee ?? 0;

  return (
    <Layout>
      <div style={{ background: 'var(--color-background)' }} className="section-padding">
        <div className="container-custom max-w-3xl">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 mb-6 text-sm hover:underline"
            style={{ color: 'var(--color-text-secondary)' }}
            data-testid="back-to-dashboard"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Dashboard
          </button>

          <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-3xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                Order #{order.order_id.slice(-8).toUpperCase()}
              </h1>
              <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                Placed on {new Date(order.created_at).toLocaleDateString('en-KE', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            </div>
            <div className="flex gap-2">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
                {order.status?.charAt(0).toUpperCase() + order.status?.slice(1)}
              </span>
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                Payment: {order.payment_status?.charAt(0).toUpperCase() + order.payment_status?.slice(1)}
              </span>
            </div>
          </div>

          {/* Items */}
          <Card className="luxury-card mb-6">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <Package className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                Items
              </h2>
              <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                {(order.items || []).map((item, idx) => (
                  <div key={idx} className="flex justify-between py-3">
                    <div>
                      <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        {item.name || item.product_id}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        Qty: {item.quantity} &times; KES {Number(item.price).toLocaleString()}
                      </p>
                    </div>
                    <p className="font-medium" style={{ color: 'var(--color-text-primary)' }}>
                      KES {(item.quantity * item.price).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>

              <div className="border-t mt-4 pt-4 space-y-2" style={{ borderColor: 'var(--color-border)' }}>
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Subtotal</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>KES {Number(subtotal).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span style={{ color: 'var(--color-text-secondary)' }}>Shipping</span>
                  <span style={{ color: 'var(--color-text-primary)' }}>
                    {shippingFee === 0 ? 'Free' : `KES ${Number(shippingFee).toLocaleString()}`}
                  </span>
                </div>
                <div className="flex justify-between font-bold text-lg pt-2">
                  <span style={{ color: 'var(--color-text-primary)' }}>Total</span>
                  <span style={{ color: 'var(--color-accent)' }}>KES {Number(order.total).toLocaleString()}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Shipping address */}
          {order.shipping_address && Object.keys(order.shipping_address).length > 0 && (
            <Card className="luxury-card mb-6">
              <CardContent className="p-6">
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                  <MapPin className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                  Shipping Address
                </h2>
                <p style={{ color: 'var(--color-text-secondary)' }}>{order.shipping_address.fullName}</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>{order.shipping_address.phone}</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>{order.shipping_address.address}</p>
                <p style={{ color: 'var(--color-text-secondary)' }}>{order.shipping_address.city}</p>
              </CardContent>
            </Card>
          )}

          {/* Payment info */}
          <Card className="luxury-card">
            <CardContent className="p-6">
              <h2 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
                <CreditCard className="w-5 h-5" style={{ color: 'var(--color-accent)' }} />
                Payment
              </h2>
              <p style={{ color: 'var(--color-text-secondary)' }}>
                Method: {order.payment_method ? order.payment_method.toUpperCase() : 'Not yet selected'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default OrderDetailPage;
